import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, Ticket, Check } from 'lucide-react';

/**
 * Swiggy-style coupon panel: manual code entry + "Available coupons" cards
 * with one-tap APPLY. Right-side sheet on desktop, bottom sheet on mobile.
 */
export function CouponDrawer({ open, cfg, onClose, onApply, busyCode, error, appliedCode }) {
  const [draft, setDraft] = useState('');
  const coupons = cfg.coupons || [];

  useEffect(() => { if (!open) setDraft(''); }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const submit = (e) => {
    e.preventDefault();
    const code = draft.trim().toUpperCase();
    if (code) onApply(code);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div key="coupon-overlay" className="fixed inset-0 z-[130] flex items-end sm:items-stretch sm:justify-end"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="coupon-drawer">
          <button type="button" aria-label="Close coupons" onClick={onClose} className="absolute inset-0 bg-slate-900/60 backdrop-blur-[2px]" data-testid="coupon-drawer-backdrop" />
          <motion.div
            className="relative w-full sm:w-[440px] sm:h-full bg-white sm:rounded-none rounded-t-3xl shadow-2xl flex flex-col max-h-[88vh] sm:max-h-none"
            initial={{ y: 40, x: 0, opacity: 0.6 }} animate={{ y: 0, x: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 380, damping: 34 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <h2 className="font-serif text-2xl text-slate-900">{cfg.coupon_drawer_title || 'Apply a coupon'}</h2>
              <button type="button" onClick={onClose} className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 transition-colors" data-testid="coupon-drawer-close"><X size={18} /></button>
            </div>

            <form onSubmit={submit} className="px-5">
              <div className="flex rounded-2xl border-2 border-slate-200 focus-within:border-slate-900 overflow-hidden transition-colors">
                <input value={draft} onChange={(e) => setDraft(e.target.value.toUpperCase())} placeholder={cfg.coupon_input_placeholder || 'Enter coupon code'} autoCapitalize="characters" autoCorrect="off" spellCheck={false}
                  className="flex-1 px-4 py-3.5 text-sm font-mono font-bold tracking-wider outline-none placeholder:font-sans placeholder:font-normal placeholder:tracking-normal" data-testid="coupon-input" />
                <button type="submit" disabled={!draft.trim() || !!busyCode} className="px-5 bg-amber-500 hover:bg-amber-400 text-slate-900 text-xs font-extrabold tracking-widest transition-colors disabled:opacity-50 min-w-[84px] flex items-center justify-center" data-testid="coupon-apply-btn">
                  {busyCode && busyCode === draft.trim().toUpperCase() ? <Loader2 size={16} className="animate-spin" /> : (cfg.label_apply || 'APPLY')}
                </button>
              </div>
              {error && <p className="text-xs font-semibold text-rose-600 mt-2" data-testid="coupon-error">{error}</p>}
            </form>

            <div className="px-5 pb-6 pt-5 overflow-y-auto flex-1">
              <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-slate-400 mb-3">{cfg.coupon_section_title || 'Available coupons'}</p>
              {coupons.length === 0 && <p className="text-sm text-slate-500">{cfg.coupon_empty_text || 'No public coupons right now — enter a code above if you have one.'}</p>}
              <div className="space-y-4">
                {coupons.map((c, i) => {
                  const isApplied = appliedCode && appliedCode.toUpperCase() === c.code;
                  return (
                    <div key={c.code} className={`relative rounded-2xl border p-4 ${c.is_best ? 'border-amber-300 bg-amber-50/40' : 'border-slate-200 bg-white'}`} data-testid={`coupon-card-${i}`}>
                      {c.badge && <span className="absolute -top-2.5 right-4 rounded-full bg-slate-900 text-white text-[10px] font-extrabold tracking-widest px-2.5 py-1">{c.badge}</span>}
                      <div className="inline-flex items-center gap-2 rounded-lg border border-dashed border-amber-400 bg-amber-50 px-3 py-1.5">
                        <Ticket size={14} className="text-amber-600" />
                        <span className="font-mono font-extrabold tracking-widest text-slate-900 text-sm" data-testid={`coupon-card-code-${i}`}>{c.code}</span>
                      </div>
                      <p className="text-lg font-extrabold text-slate-900 mt-3">{c.headline}</p>
                      {c.description && <p className="text-sm text-slate-500 mt-1 leading-relaxed">{c.description}</p>}
                      <button type="button" onClick={() => onApply(c.code)} disabled={!!busyCode || isApplied}
                        className={`mt-4 inline-flex items-center justify-center gap-1.5 rounded-xl border-2 px-4 py-2 text-xs font-extrabold tracking-widest transition-colors min-w-[150px] ${isApplied ? 'border-emerald-500 text-emerald-700 bg-emerald-50' : 'border-amber-500 text-amber-700 hover:bg-amber-500 hover:text-slate-900'} disabled:opacity-70`}
                        data-testid={`coupon-card-apply-${i}`}>
                        {busyCode === c.code ? <Loader2 size={14} className="animate-spin" /> : isApplied ? <><Check size={14} />{cfg.coupon_card_applied_label || 'APPLIED'}</> : (cfg.coupon_card_apply_label || 'APPLY COUPON')}
                      </button>
                      {i < coupons.length - 1 && <div className="absolute left-4 right-4 -bottom-2 border-t border-dashed border-slate-200" />}
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
